import { useState } from 'react';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

// project imports
import Search from './Search';
import Profile from './Profile';
import Notification from './Notification';
import MobileSection from './MobileSection';
import FirebaseRegister from 'sections/auth/AuthRegister';
import DoctorsRegistar from 'sections/auth/DoctorsRegistar';
import { useBranding } from 'contexts/BrandingContext';

// project import
import { PlusOutlined, CopyOutlined, QuestionOutlined } from '@ant-design/icons';

// ==============================|| HEADER - CONTENT ||============================== //

export default function HeaderContent() {
  const role = localStorage.getItem('role');
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));
  const { slug, customDomain } = useBranding();
  const [openRegisterModal, setOpenRegisterModal] = useState(false);
  const [showCopySuccess, setShowCopySuccess] = useState(false);

  const handleOpenRegister = () => setOpenRegisterModal(true);
  const handleCloseRegister = () => setOpenRegisterModal(false);

  const handleCopyAppointmentUrl = () => {
    const tenantId = localStorage.getItem('tenant_id');
    // Prefer custom domain if configured; else use the default
    // appointments.zoiteckh.com/appointment/{slug} URL.
    const appointmentUrl = customDomain
      ? `https://${customDomain}`
      : `${window.location.origin}/appointment/${slug || tenantId}`;

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(appointmentUrl)
        .then(() => {
          setShowCopySuccess(true);
          window.open(appointmentUrl, '_blank');
        })
        .catch(() => fallbackCopy(appointmentUrl));
    } else {
      fallbackCopy(appointmentUrl);
    }
  };

  const fallbackCopy = (text) => {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.cssText = 'position:fixed;left:-9999px';
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
      setShowCopySuccess(true);
      window.open(text, '_blank');
    } catch { /* ignore */ }
    document.body.removeChild(ta);
  };

  return (
    <>
      {!downLG && <Search />}
      {downLG && <Box sx={{ width: '100%', ml: 1 }} />}

      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0, ml: 1 }}>
        <Tooltip title={role === 'SUPER_ADMIN' ? 'Add Company' : 'Add Doctor'}>
          <IconButton
            onClick={handleOpenRegister}
            sx={{
              width: 36,
              height: 36,
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 2,
              color: 'text.primary',
              '&:hover': { bgcolor: 'grey.100' },
            }}
          >
            <PlusOutlined style={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>

        {role === 'COMPANY_ADMIN' && (
          <Tooltip title="Setup guide">
            <IconButton
              onClick={() => window.dispatchEvent(new Event('open-welcome-setup'))}
              sx={{ width: 36, height: 36, border: '1px solid', borderColor: 'grey.300', borderRadius: 2, color: 'text.primary', '&:hover': { bgcolor: 'grey.100' } }}
            >
              <QuestionOutlined style={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}

        {role !== 'SUPER_ADMIN' && (
          <Button
            variant="contained"
            size="small"
            startIcon={<CopyOutlined />}
            onClick={handleCopyAppointmentUrl}
            sx={{
              height: 36,
              whiteSpace: 'nowrap',
              fontSize: '0.8125rem',
              fontWeight: 500,
              px: 2,
              borderRadius: 2,
              bgcolor: '#0D9488',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#0F766E', boxShadow: 'none' },
            }}
          >
            Appointment URL
          </Button>
        )}

        <Notification />
        {!downLG && <Profile />}
        {downLG && <MobileSection />}
      </Stack>

      <Dialog open={openRegisterModal} onClose={handleCloseRegister} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h4">
            {role === 'SUPER_ADMIN' ? 'Register Company' : 'Register Doctor'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid size={12}>
              {role === 'SUPER_ADMIN' ? (
                <FirebaseRegister onSuccess={handleCloseRegister} />
              ) : (
                <DoctorsRegistar onSuccess={handleCloseRegister} />
              )}
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={showCopySuccess}
        autoHideDuration={3000}
        onClose={() => setShowCopySuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowCopySuccess(false)} severity="success" variant="filled">
          Appointment URL copied!
        </Alert>
      </Snackbar>
    </>
  );
}
